import { z } from "zod";

/**
 * The Voice QA color taxonomy: the one deterministic color every evaluated call carries, shared by
 * the LLM judge prompt, Comms QA tasks, Slack alerting, and alert sampling.
 *
 * Internal only. The color is our read on how the agent performed, so it stays on ops-facing
 * surfaces — never the customer-facing Calls view or its CSV export.
 *
 * This module is pure vocabulary — the colors, the rules, and the rule → color binding. The
 * classifier that applies the rules to a call lives in `src/lib/voice-qa-taxonomy.ts`, and the
 * written spec (including the multi-intent and partial-completion cases) is
 * `docs/voice-qa-color-taxonomy.md`.
 */

/**
 * Bump whenever a rule change would give an already-classified call a different color. Stored
 * classifications keep the version they were written under, so an audit query can tell "this call
 * was green" apart from "this call was green under the old rules".
 *
 * v1 — the four-value `VoiceAlertSeverity` (critical / warning / no_connect / healthy).
 * v2 — five colors: healthy split into blue (action taken) and green (no action taken), and
 *      no-connect widened from instant abandon to also cover an immediate escalation.
 */
export const VOICE_QA_TAXONOMY_VERSION = 2;

/** Declaration order is precedence order for the two review bands; see VOICE_QA_RULE_COLOR. */
export enum VoiceQaColor {
  RED = "red",
  ORANGE = "orange",
  WHITE = "white",
  BLUE = "blue",
  GREEN = "green",
}

/**
 * `slackColor` is the Slack attachment color bar; `detail` is whether an alert for this color
 * renders the full judge payload (reasoning + suggestions) or just the summary line.
 */
export const VOICE_QA_COLOR_META: Record<
  VoiceQaColor,
  { emoji: string; slackColor: string; label: string; definition: string; detail: boolean }
> = {
  [VoiceQaColor.RED]: {
    emoji: "🔴",
    slackColor: "#D32F2F",
    label: "CRITICAL",
    definition: "Critical — requires mitigation.",
    detail: true,
  },
  [VoiceQaColor.ORANGE]: {
    emoji: "🟠",
    slackColor: "#E8A33D",
    label: "NEEDS REVIEW",
    definition: "Review needed because of a quality issue.",
    detail: true,
  },
  [VoiceQaColor.WHITE]: {
    emoji: "⚪",
    slackColor: "#9AA0A6",
    label: "NO CONNECT",
    definition: "No connect — the caller left or reached a human before the agent did any work.",
    detail: false,
  },
  [VoiceQaColor.BLUE]: {
    emoji: "🔵",
    slackColor: "#3B7DD8",
    label: "ACTION TAKEN",
    definition: "OK, action taken — a booking, reschedule, cancellation, confirmation, or a message left.",
    detail: false,
  },
  [VoiceQaColor.GREEN]: {
    emoji: "🟢",
    slackColor: "#2EB67D",
    label: "OK",
    definition: "OK, no action taken.",
    detail: false,
  },
};

/** Every reason a call can end up at its color. One rule per distinct signal, so alerts cite it. */
export enum VoiceQaRule {
  // Red — the system or the agent failed the caller.
  AUDIO_PLAYBACK_FAILURE = "audio_playback_failure",
  TTS_ERROR_ESCALATION = "tts_error_escalation",
  TOOL_ERROR = "tool_error",
  TOOL_ERROR_ESCALATION = "tool_error_escalation",
  AGENT_STALLED = "agent_stalled",
  LOW_SAFETY = "low_safety",
  // Orange — the call worked, but the quality of it needs a human look.
  TRANSFER_NOT_CONFIGURED = "transfer_not_configured",
  LOW_WORKFLOW_EFFICIENCY = "low_workflow_efficiency",
  LOW_CSAT = "low_csat",
  LOW_CONVERSATIONAL_QUALITY = "low_conversational_quality",
  AVOIDABLE_ESCALATION = "avoidable_escalation",
  // Blue / white / green — the normal outcomes, mutually exclusive rather than ranked.
  ACTION_COMPLETED = "action_completed",
  IMMEDIATE_ABANDON = "immediate_abandon",
  IMMEDIATE_ESCALATION = "immediate_escalation",
  NO_ACTION_NEEDED = "no_action_needed",
}

/**
 * The taxonomy itself: which color each rule produces. The classifier reads the color off this
 * table rather than deciding it at the call site, so a rule's color is defined in exactly one
 * place and the same table renders the judge prompt and the docs.
 */
export const VOICE_QA_RULE_COLOR: Record<VoiceQaRule, VoiceQaColor> = {
  [VoiceQaRule.AUDIO_PLAYBACK_FAILURE]: VoiceQaColor.RED,
  [VoiceQaRule.TTS_ERROR_ESCALATION]: VoiceQaColor.RED,
  [VoiceQaRule.TOOL_ERROR]: VoiceQaColor.RED,
  [VoiceQaRule.TOOL_ERROR_ESCALATION]: VoiceQaColor.RED,
  [VoiceQaRule.AGENT_STALLED]: VoiceQaColor.RED,
  [VoiceQaRule.LOW_SAFETY]: VoiceQaColor.RED,
  [VoiceQaRule.TRANSFER_NOT_CONFIGURED]: VoiceQaColor.ORANGE,
  [VoiceQaRule.LOW_WORKFLOW_EFFICIENCY]: VoiceQaColor.ORANGE,
  [VoiceQaRule.LOW_CSAT]: VoiceQaColor.ORANGE,
  [VoiceQaRule.LOW_CONVERSATIONAL_QUALITY]: VoiceQaColor.ORANGE,
  [VoiceQaRule.AVOIDABLE_ESCALATION]: VoiceQaColor.ORANGE,
  [VoiceQaRule.ACTION_COMPLETED]: VoiceQaColor.BLUE,
  [VoiceQaRule.IMMEDIATE_ABANDON]: VoiceQaColor.WHITE,
  [VoiceQaRule.IMMEDIATE_ESCALATION]: VoiceQaColor.WHITE,
  [VoiceQaRule.NO_ACTION_NEEDED]: VoiceQaColor.GREEN,
};

export const VOICE_QA_RULE_DESCRIPTIONS: Record<VoiceQaRule, string> = {
  [VoiceQaRule.AUDIO_PLAYBACK_FAILURE]: "the runtime failed to play the agent's speech to the caller.",
  [VoiceQaRule.TTS_ERROR_ESCALATION]: "the call was escalated because speech synthesis failed.",
  [VoiceQaRule.TOOL_ERROR]: "a tool returned an unexpected error, or the data a tool needed was missing.",
  [VoiceQaRule.TOOL_ERROR_ESCALATION]:
    "the call was escalated because of a tool error, with no audited failure to name.",
  [VoiceQaRule.AGENT_STALLED]: "the agent left the caller in dead air past the stuck threshold.",
  [VoiceQaRule.LOW_SAFETY]: "the judge scored safety at or below the low-score threshold.",
  [VoiceQaRule.TRANSFER_NOT_CONFIGURED]:
    "representative transfer is not wired up yet for a tenant that has not gone live.",
  [VoiceQaRule.LOW_WORKFLOW_EFFICIENCY]: "the judge scored workflow efficiency at or below the low-score threshold.",
  [VoiceQaRule.LOW_CSAT]: "the judge scored caller satisfaction at or below the low-score threshold.",
  [VoiceQaRule.LOW_CONVERSATIONAL_QUALITY]:
    "the judge scored conversational quality at or below the low-score threshold.",
  [VoiceQaRule.AVOIDABLE_ESCALATION]:
    "the call was escalated for something we should have handled (the patient or their appointment could not be found).",
  [VoiceQaRule.ACTION_COMPLETED]:
    "an action tool completed for the caller — a booking, reschedule, cancellation, confirmation, appointment-request completion, or a message left for staff.",
  [VoiceQaRule.IMMEDIATE_ABANDON]:
    "no meaningful dialogue happened at all (workflow efficiency 0) — an instant hang-up or silence.",
  [VoiceQaRule.IMMEDIATE_ESCALATION]:
    "the caller reached a human before the agent did any work — a keypad routing choice, or asking for a person up front.",
  [VoiceQaRule.NO_ACTION_NEEDED]:
    "the agent handled the call without taking an action — a question answered, a legitimate handoff, or a caller who chose not to proceed.",
};

export const voiceQaFindingSchema = z.object({
  rule: z.nativeEnum(VoiceQaRule),
  /** Human-readable detail for this finding, rendered in Slack and in the QA task. */
  detail: z.string(),
});

export type VoiceQaFinding = z.infer<typeof voiceQaFindingSchema>;

/**
 * The predicate inputs the blue/white/green decision table consumed. Stored alongside the color so
 * a reviewer can see why a call landed where it did without re-running the classifier.
 */
export const voiceQaSignalsSchema = z.object({
  /** An action tool completed successfully — the blue predicate. */
  actionTaken: z.boolean(),
  /** Canonical names of the action tools that succeeded. */
  actionTools: z.array(z.string()),
  /** The call never reached meaningful dialogue — the white predicate. */
  noConnect: z.boolean(),
});

export type VoiceQaSignals = z.infer<typeof voiceQaSignalsSchema>;

export const voiceQaClassificationSchema = z.object({
  taxonomyVersion: z.number().int(),
  color: z.nativeEnum(VoiceQaColor),
  /** Every rule that fired for the winning color, in taxonomy order. Never empty. */
  findings: z.array(voiceQaFindingSchema),
  signals: voiceQaSignalsSchema,
  /** Replaces the judge's transfer reason in alerts when runtime evidence contradicts it. */
  transferReasonOverride: z.string().nullish(),
});

export type VoiceQaClassification = z.infer<typeof voiceQaClassificationSchema>;

/** Flattened finding details, for the Slack "Why" line and the QA task badge. */
export function voiceQaReasons(classification: Pick<VoiceQaClassification, "findings">): string[] {
  return classification.findings.map((finding) => finding.detail);
}

/**
 * Renders the taxonomy as text, for the judge prompt and the docs. Generated from the tables above
 * so a prompt can never describe a rule the classifier does not apply.
 */
export function formatVoiceQaTaxonomy(): string {
  return Object.values(VoiceQaColor)
    .map((color) => {
      const meta = VOICE_QA_COLOR_META[color];
      const rules = Object.values(VoiceQaRule).filter((rule) => VOICE_QA_RULE_COLOR[rule] === color);

      return [
        `- ${color} (${meta.label}) — ${meta.definition}`,
        ...rules.map((rule) => `    - ${rule}: ${VOICE_QA_RULE_DESCRIPTIONS[rule]}`),
      ].join("\n");
    })
    .join("\n");
}
